/**
 * Deutsche Texte für die eingebaute Formularprüfung (`required`, `min`, `type="email"` …).
 *
 * Die Hinweisblasen des Browsers folgen dessen Spracheinstellung, nicht `lang="de"` der Seite – ein englischer
 * Browser zeigt „Please fill out this field.“. Ein Listener am Dokument setzt deshalb beim Prüfen die Meldung
 * selbst; jede Eingabe löscht sie wieder, damit der Browser neu prüft. So gilt es für alle Formulare, ohne dass
 * einzelne Felder etwas tun müssen.
 */
type Feld = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const istFeld = (el: EventTarget | null): el is Feld =>
  el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;

const istZeitlich = (el: Feld) => el instanceof HTMLInputElement && ['date', 'time', 'datetime-local', 'month', 'week'].includes(el.type);

/** Datum/Zeit-Grenzen so zeigen, wie man sie liest: 2026-10-15 → 15.10.2026. */
const grenze = (el: Feld, wert: string) => {
  const datum = /^(\d{4})-(\d{2})-(\d{2})/.exec(wert);
  return istZeitlich(el) && datum ? `${datum[3]}.${datum[2]}.${datum[1]}` : wert.replace('.', ',');
};

export const meldung = (el: Feld): string => {
  const v = el.validity;
  if (v.valueMissing) {
    if (el instanceof HTMLInputElement && el.type === 'checkbox') return 'Bitte setzen Sie dieses Häkchen.';
    if (el instanceof HTMLInputElement && el.type === 'radio') return 'Bitte wählen Sie eine der Möglichkeiten.';
    if (el instanceof HTMLInputElement && el.type === 'file') return 'Bitte wählen Sie eine Datei aus.';
    if (el instanceof HTMLSelectElement) return 'Bitte wählen Sie einen Eintrag aus.';
    return 'Bitte füllen Sie dieses Feld aus.';
  }
  if (v.badInput) {
    if (el instanceof HTMLInputElement && el.type === 'number') return 'Bitte geben Sie eine Zahl ein.';
    return 'Bitte geben Sie einen gültigen Wert ein.';
  }
  if (v.typeMismatch) {
    if (el instanceof HTMLInputElement && el.type === 'email') return 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    if (el instanceof HTMLInputElement && el.type === 'url') return 'Bitte geben Sie eine gültige Webadresse ein.';
    return 'Bitte geben Sie einen gültigen Wert ein.';
  }
  if (v.patternMismatch) return el.title || 'Bitte halten Sie sich an das vorgegebene Format.';
  if (v.tooShort && !(el instanceof HTMLSelectElement)) {
    return `Bitte geben Sie mindestens ${el.minLength} Zeichen ein (derzeit ${el.value.length}).`;
  }
  if (v.tooLong && !(el instanceof HTMLSelectElement)) {
    return `Bitte geben Sie höchstens ${el.maxLength} Zeichen ein (derzeit ${el.value.length}).`;
  }
  if (v.rangeUnderflow && el instanceof HTMLInputElement) {
    return istZeitlich(el) ? `Bitte frühestens ${grenze(el, el.min)} angeben.` : `Der Wert muss mindestens ${grenze(el, el.min)} sein.`;
  }
  if (v.rangeOverflow && el instanceof HTMLInputElement) {
    return istZeitlich(el) ? `Bitte spätestens ${grenze(el, el.max)} angeben.` : `Der Wert darf höchstens ${grenze(el, el.max)} sein.`;
  }
  if (v.stepMismatch && el instanceof HTMLInputElement) {
    return `Bitte geben Sie einen Wert in Schritten von ${grenze(el, el.step)} ein.`;
  }
  return 'Bitte prüfen Sie diese Eingabe.';
};

/** Einmal beim Start aufrufen (index.tsx). */
export const installiereDeutscheValidierung = () => {
  document.addEventListener('invalid', e => {
    const el = e.target;
    if (!istFeld(el)) return;
    // Erst leeren: eine alte eigene Meldung hielte das Feld sonst ungültig und verdeckte den echten Grund.
    el.setCustomValidity('');
    if (!el.validity.valid) el.setCustomValidity(meldung(el));
  }, true);

  const zuruecksetzen = (e: Event) => { if (istFeld(e.target)) e.target.setCustomValidity(''); };
  document.addEventListener('input', zuruecksetzen, true);
  document.addEventListener('change', zuruecksetzen, true);

  // Felder, die per Knopf gefüllt werden („Jetzt“), feuern kein `input` – vor dem Absenden (der Klick kommt vor
  // der Prüfung, auch beim Absenden per Enter) deshalb alle Meldungen des Formulars leeren.
  document.addEventListener('click', e => {
    const knopf = e.target instanceof Element ? e.target.closest('button, input[type="submit"]') : null;
    const form = (knopf as HTMLButtonElement | HTMLInputElement | null)?.form;
    if (!knopf || !form || (knopf as HTMLButtonElement).type !== 'submit') return;
    Array.from(form.elements).forEach(el => { if (istFeld(el)) el.setCustomValidity(''); });
  }, true);
};
